






















































/* General */
import Vue from 'vue'
import { debounce } from 'lodash'

/* Mixins */

/* Components */

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import ColorAssistant from '@/helpers/color-assistant'
import AppSumoAssistant from '@/helpers/appsumo-assistant'

/* Data types */
import { EEntities } from '@/types'
import { IAccountMembershipsInputInviteToProject } from '@/api/types/request.types'
import { IPersonWithEmail } from '@/api/types/response.types'

/* API */
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'
import AccountMembershipsAPI from '@/api/modules/account-memberships.api'

export default Vue.extend({
  name: 'QuickAddMemberPicker',

  props: {
    entityType: {
      type: String as () => EEntities,
      required: true,
    },
    entityId: {
      type: Number,
      required: true,
    },
    placement: {
      type: String,
      default: 'bottom',
    },
    stumpy: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      AppSumoAssistant,
      isLoading: true,
      isSubmitting: false,
      candidates: [] as IPersonWithEmail[],
      selectedCandidateIDs: [] as number[],
      searchQuery: null,
      debounceAddMembers: null,
    }
  },

  computed: {
    buttonColor(): string {
      return ColorAssistant.getEntityColor(this.entityType)
    },
    placeholderText(): string {
      const showGuests =
        this.entityType === EEntities.PROJECTS &&
        this.$permissions.isTieredPricingPlan
      if (this.isLoading) {
        return ''
      }
      if (this.hasCandidatesToDisplay) {
        return `Add by name or invite ${
          showGuests ? 'guest ' : 'user '
        }by email...`
      }
      return `Invite ${showGuests ? 'guest ' : 'user '}by email...`
    },
    candidatesToDisplay(): IPersonWithEmail[] {
      if (!this.searchQuery) {
        return this.candidates
      }
      return this.candidates.filter(
        p =>
          p.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          p.email.toLowerCase().includes(this.searchQuery.toLowerCase()),
      )
    },
    hasCandidatesToDisplay(): boolean {
      return this.candidatesToDisplay.length > 0
    },
    hasSelectedCandidates(): boolean {
      return this.selectedCandidateIDs.length > 0
    },
    candidateSubmitText(): string {
      return this.selectedCandidateIDs.length <= 1
        ? 'Add member'
        : 'Add ' + this.selectedCandidateIDs.length + ' members'
    },
    searchQueryIsEmail(): boolean {
      if (!this.searchQuery) {
        return false
      }
      return TextAssistant.isValidEmail(this.searchQuery)
    },
    entityTypeName(): string {
      return TextAssistant.entitySingularName(this.entityType).toLowerCase()
    },
    noResultsText(): string {
      if (this.searchQuery) {
        return 'No results found. Enter an email to invite someone new.'
      }
      /* Case where there are simply no candidates */
      return 'Enter an email to invite someone new.'
    },
  },

  created() {
    this.debounceAddMembers = debounce(this.addMembers, 500)
  },

  methods: {
    async toggleCandidate(id: number) {
      if (this.isAdded(id)) {
        this.selectedCandidateIDs = this.selectedCandidateIDs.filter(
          p => p !== id,
        )
      } else {
        this.selectedCandidateIDs.push(id)
      }

      await this.debounceAddMembers()
    },
    isAdded(id: number): boolean {
      return this.selectedCandidateIDs.includes(id)
    },
    resetCandidates() {
      this.searchQuery = null
      this.selectedCandidateIDs = []
    },
    async getCandidates() {
      this.isLoading = true
      if (this.entityType === EEntities.MAILBOXES) {
        this.candidates = await MailboxesAPI.get_MembershipCandidates(
          this.entityId,
        )
      } else {
        this.candidates = await InternalProjectsAPI.get_MembershipCandidates(
          this.entityId,
        )
      }
      this.isLoading = false
    },
    async addMembers() {
      try {
        if (!this.hasSelectedCandidates) {
          return
        }
        this.isSubmitting = true
        let response
        if (this.entityType === EEntities.MAILBOXES) {
          response = await MailboxesAPI.put_AddPeople(
            this.entityId,
            this.selectedCandidateIDs,
          )
        } else {
          response = await InternalProjectsAPI.put_AddPeople(
            this.entityId,
            this.selectedCandidateIDs,
          )
        }
        this.$messaging.showSuccess(response.message)

        /* Remove from assignable people */
        this.candidates = this.candidates.filter(
          p => !this.selectedCandidateIDs.includes(p.id),
        )
        this.$emit('members-added')
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.resetCandidates()
        this.isSubmitting = false
      }
    },
    async inviteMember() {
      try {
        if (
          !(await AppSumoAssistant.isUnderAccountMembershipsLimit(
            this.$router,
            this.$permissions,
          ))
        ) {
          return
        }

        this.isSubmitting = true
        const data = {
          email: this.searchQuery,
          projectID: this.entityId,
        } as IAccountMembershipsInputInviteToProject

        const response = await AccountMembershipsAPI.post_InviteToProject(data)
        this.$messaging.showSuccess(response.message)
        this.$emit('members-added')
        this.resetCandidates()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isSubmitting = false
      }
    },
  },
})
